import { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  ChevronDown, CheckCircle2, Monitor, Smartphone, Globe,
  Download, Music2, Video, Zap, ShieldCheck, Clock,
} from 'lucide-react'

// ─── Helpers ──────────────────────────────────────────────────────
function FAQItem({ q, a, isOpen, toggle }) {
  return (
    <div className="border border-border/60 rounded-xl overflow-hidden bg-card/50">
      <button
        onClick={toggle}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left font-semibold text-sm sm:text-base hover:bg-primary/5 transition-colors"
        aria-expanded={isOpen}
      >
        <span className="text-foreground">{q}</span>
        <ChevronDown className={`w-4 h-4 flex-shrink-0 text-muted-foreground transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <div className="px-5 pb-4 text-sm text-muted-foreground leading-relaxed border-t border-border/40 pt-3">
          {a}
        </div>
      )}
    </div>
  )
}

function H2({ id, children }) {
  return (
    <h2 id={id} className="text-xl sm:text-2xl font-bold text-foreground mb-5 pb-3 border-b border-border/50">
      {children}
    </h2>
  )
}

function H3({ children }) {
  return <h3 className="text-base sm:text-lg font-bold text-foreground mb-3">{children}</h3>
}

// ─── Main component ───────────────────────────────────────────────
export default function VideoDownloaderSEO({ provider }) {
  const [openFAQ, setOpenFAQ] = useState(null)
  const toggle = (i) => setOpenFAQ(p => p === i ? null : i)
  const d = DATA[provider] || DATA.youtube

  return (
    <article className="space-y-16 text-sm sm:text-base">

      {/* ── What it is ──────────────────────────────────────────── */}
      <section aria-labelledby="what-is">
        <H2 id="what-is">{d.whatTitle}</H2>
        <p className="text-muted-foreground leading-relaxed mb-4">{d.whatBody}</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
          {[
            { icon: Zap,         color: 'text-primary',    bg: 'bg-primary/10',    label: 'Lightning Fast',   detail: 'Cloud servers process your request in seconds.' },
            { icon: ShieldCheck, color: 'text-green-500',  bg: 'bg-green-500/10',  label: 'Safe & Private',   detail: 'No files stored. No account required.' },
            { icon: Download,    color: 'text-accent',     bg: 'bg-accent/10',     label: 'Free Forever',     detail: 'Unlimited downloads at no cost.' },
          ].map(({ icon: Icon, color, bg, label, detail }) => (
            <div key={label} className="flex items-start gap-3 p-4 bg-card rounded-xl border border-border/50">
              <div className={`w-9 h-9 rounded-lg ${bg} flex items-center justify-center flex-shrink-0`}>
                <Icon className={`w-4 h-4 ${color}`} />
              </div>
              <div>
                <p className="font-semibold text-foreground text-sm">{label}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{detail}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Supported formats table ──────────────────────────────── */}
      <section aria-labelledby="formats">
        <H2 id="formats">Supported Formats &amp; Quality Options</H2>
        <p className="text-muted-foreground leading-relaxed mb-5">{d.formatsIntro}</p>
        <div className="overflow-hidden rounded-2xl border border-border/60">
          <div className="grid grid-cols-3 bg-gradient-to-r from-primary to-accent px-4 py-2.5">
            <span className="text-xs font-bold text-white uppercase tracking-wider">Format</span>
            <span className="text-xs font-bold text-white uppercase tracking-wider text-center">Quality</span>
            <span className="text-xs font-bold text-white uppercase tracking-wider text-right">Best For</span>
          </div>
          {/* Audio rows */}
          <div className="px-4 py-1.5 bg-muted/30 flex items-center gap-1.5">
            <Music2 className="w-3 h-3 text-primary" />
            <span className="text-[10px] font-bold text-primary uppercase tracking-widest">Audio</span>
          </div>
          {[
            { fmt: 'MP3', quality: '320 kbps', best: 'Music & HQ listening' },
            { fmt: 'MP3', quality: '192 kbps', best: 'Podcasts & general use' },
            { fmt: 'MP3', quality: '128 kbps', best: 'Small file size' },
          ].map((r, i) => (
            <div key={i} className="grid grid-cols-3 px-4 py-3 border-t border-border/30 hover:bg-primary/3 transition-colors">
              <span className="font-semibold text-foreground text-sm">{r.fmt}</span>
              <span className="text-center text-sm text-muted-foreground">{r.quality}</span>
              <span className="text-right text-xs text-muted-foreground">{r.best}</span>
            </div>
          ))}
          {/* Video rows */}
          <div className="px-4 py-1.5 bg-muted/30 flex items-center gap-1.5 border-t border-border/40 mt-1">
            <Video className="w-3 h-3 text-accent" />
            <span className="text-[10px] font-bold text-accent uppercase tracking-widest">Video</span>
          </div>
          {d.videoFormats.map((r, i) => (
            <div key={i} className="grid grid-cols-3 px-4 py-3 border-t border-border/30 hover:bg-accent/3 transition-colors">
              <span className="font-semibold text-foreground text-sm flex items-center gap-1.5">
                MP4
                {r.badge && (
                  <span className="text-[9px] px-1.5 py-0.5 rounded bg-accent/15 text-accent font-bold uppercase">{r.badge}</span>
                )}
              </span>
              <span className="text-center text-sm text-muted-foreground">{r.quality}</span>
              <span className="text-right text-xs text-muted-foreground">{r.best}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ── Step-by-step guide ───────────────────────────────────── */}
      <section aria-labelledby="how-to">
        <H2 id="how-to">{d.howToTitle}</H2>
        <p className="text-muted-foreground leading-relaxed mb-6">{d.howToIntro}</p>
        <ol className="space-y-4">
          {d.steps.map((step, i) => (
            <li key={i} className="flex gap-4 items-start p-4 bg-card rounded-xl border border-border/50 hover:border-primary/30 transition-colors">
              <span className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-primary to-accent text-white font-bold text-sm flex items-center justify-center shadow-sm">
                {i + 1}
              </span>
              <div className="pt-0.5">
                <p className="font-semibold text-foreground mb-1">{step.title}</p>
                <p className="text-muted-foreground leading-relaxed text-sm">{step.text}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      {/* ── Mobile vs Desktop ────────────────────────────────────── */}
      <section aria-labelledby="devices">
        <H2 id="devices">Downloading on Mobile &amp; Desktop</H2>
        <p className="text-muted-foreground leading-relaxed mb-6">{d.devicesIntro}</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
          {[
            { icon: Smartphone, label: 'iPhone / iOS',      body: d.deviceiOS },
            { icon: Smartphone, label: 'Android',           body: d.deviceAndroid },
            { icon: Monitor,    label: 'Windows / Mac / Linux', body: d.deviceDesktop },
          ].map(({ icon: Icon, label, body }) => (
            <div key={label} className="p-5 bg-card rounded-2xl border border-border/50 hover:border-primary/30 transition-colors">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Icon className="w-4 h-4 text-primary" />
                </div>
                <H3>{label}</H3>
              </div>
              <p className="text-muted-foreground text-sm leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Platform tips ────────────────────────────────────────── */}
      <section aria-labelledby="tips">
        <H2 id="tips">{d.tipsTitle}</H2>
        <p className="text-muted-foreground leading-relaxed mb-5">{d.tipsIntro}</p>
        <ul className="space-y-3">
          {d.tips.map((tip, i) => (
            <li key={i} className="flex items-start gap-3 p-4 bg-card rounded-xl border border-border/50">
              <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
              <div>
                <span className="font-semibold text-foreground">{tip.title}: </span>
                <span className="text-muted-foreground">{tip.body}</span>
              </div>
            </li>
          ))}
        </ul>
      </section>

      {/* ── Why FastAudio ────────────────────────────────────────── */}
      <section aria-labelledby="why-fastaudio">
        <H2 id="why-fastaudio">Why Choose FastAudio?</H2>
        <p className="text-muted-foreground leading-relaxed mb-5">
          Dozens of video downloader tools exist online. Here is why FastAudio stands out for {d.platformName} users:
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            { icon: Zap,         color: 'text-primary',   bg: 'bg-primary/10',   title: 'No Software Needed',      body: 'Works entirely in your browser — no Chrome extension, no desktop app, no plugins to install.' },
            { icon: ShieldCheck, color: 'text-green-500', bg: 'bg-green-500/10', title: 'Privacy First',            body: 'We never store your downloaded files. All conversions happen on server and files are deleted immediately.' },
            { icon: Music2,      color: 'text-accent',    bg: 'bg-accent/10',    title: 'Dual Format Support',      body: 'Download as MP3 audio (up to 320kbps) or MP4 video — one tool for both formats.' },
            { icon: Globe,       color: 'text-blue-500',  bg: 'bg-blue-500/10',  title: 'No Account Required',      body: 'No sign-up, no email, no subscription. Paste a URL and download — it is that simple.' },
            { icon: Clock,       color: 'text-orange-500',bg: 'bg-orange-500/10',title: 'Fast Processing',          body: 'Cloud-powered servers handle conversions in seconds, even for long videos.' },
            { icon: Download,    color: 'text-emerald-500',bg:'bg-emerald-500/10',title: 'Unlimited Downloads',     body: 'No daily caps, no queue. Download as many videos as you need at zero cost.' },
          ].map(({ icon: Icon, color, bg, title, body }) => (
            <div key={title} className="flex items-start gap-3 p-4 bg-card rounded-xl border border-border/50">
              <div className={`w-9 h-9 rounded-lg ${bg} flex items-center justify-center flex-shrink-0`}>
                <Icon className={`w-4 h-4 ${color}`} />
              </div>
              <div>
                <p className="font-semibold text-foreground text-sm mb-0.5">{title}</p>
                <p className="text-muted-foreground text-sm leading-relaxed">{body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── FAQ ─────────────────────────────────────────────────── */}
      <section aria-labelledby="faq">
        <H2 id="faq">Frequently Asked Questions</H2>
        <div className="space-y-2">
          {d.faqs.map(({ q, a }, i) => (
            <FAQItem key={i} q={q} a={a} isOpen={openFAQ === i} toggle={() => toggle(i)} />
          ))}
        </div>
      </section>

      {/* ── Internal links — topical cluster ────────────────────── */}
      <section aria-labelledby="related" className="bg-card/50 rounded-2xl border border-border/50 p-6">
        <H2 id="related">Related Downloaders &amp; Tools</H2>
        <p className="text-muted-foreground mb-5 text-sm">
          FastAudio supports all major video platforms. Explore our other free downloaders:
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {d.relatedLinks.map(({ href, label, desc }) => (
            <Link
              key={href}
              to={href}
              className="flex items-start gap-3 p-4 bg-background rounded-xl border border-border/50 hover:border-primary/40 hover:bg-primary/3 transition-all group"
            >
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                <Download className="w-3.5 h-3.5 text-primary" />
              </div>
              <div>
                <p className="font-semibold text-foreground text-sm group-hover:text-primary transition-colors">{label}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

    </article>
  )
}

// ─── Per-provider content data ────────────────────────────────────
const DATA = {

  // ────────────────── YouTube ──────────────────────────────────────
  youtube: {
    platformName: 'YouTube',

    whatTitle: 'What Is a YouTube Video Downloader?',
    whatBody:
      'A YouTube video downloader is a web tool that extracts the video or audio stream from any YouTube URL and saves it to your device. FastAudio lets you download any YouTube video as an MP4 file (up to 1080p HD) or as an MP3 audio file (up to 320kbps) — entirely free, without any software installation. It works on smartphones, tablets, and desktop computers through any modern browser.',

    formatsIntro:
      'FastAudio gives you full control over the output format and quality. Choose MP3 for music, podcasts, or audio extraction, or pick an MP4 resolution for video — from data-friendly 360p up to crisp 1080p Full HD.',
    videoFormats: [
      { quality: '1080p Full HD', best: 'TV / large screens',   badge: 'FHD' },
      { quality: '720p HD',       best: 'Laptop / tablet',       badge: 'HD' },
      { quality: '480p SD',       best: 'Mobile-friendly',       badge: null },
      { quality: '360p',          best: 'Low-data usage',         badge: null },
    ],

    howToTitle: 'How to Download YouTube Videos in MP4 or MP3',
    howToIntro:
      'Follow these simple steps to download any YouTube video. The entire process takes less than 30 seconds.',
    steps: [
      { title: 'Copy the YouTube URL',   text: 'Open YouTube and find the video you want. Copy the URL from the address bar, or tap Share → Copy Link inside the YouTube app.' },
      { title: 'Paste into FastAudio',   text: 'Click the search box at the top of this page and paste the URL (Ctrl+V on desktop, long-press → Paste on mobile).' },
      { title: 'Wait for video info',    text: 'FastAudio will automatically fetch the video title, thumbnail, and duration. This usually takes 2–5 seconds.' },
      { title: 'Choose MP4 or MP3',      text: 'Select the quality you want from the table: pick a video resolution for MP4, or choose 128/192/320kbps for MP3 audio.' },
      { title: 'Click ⚡ Download',       text: 'Hit the Download button next to your chosen format. The file will be processed and saved directly to your device.' },
    ],

    devicesIntro: 'FastAudio is a fully browser-based tool — there is nothing to install. It works identically on every platform.',
    deviceIOS:     'Open Safari on iPhone or iPad, paste the YouTube URL, select your format, and tap Download. iOS will save the MP3 to Files or MP4 to Photos depending on your format.',
    deviceAndroid: 'Open Chrome or Firefox on your Android device. Paste the YouTube URL, select MP4 or MP3, and tap Download. The file saves to your Downloads folder.',
    deviceDesktop: 'Open any desktop browser (Chrome, Firefox, Safari, Edge). Paste the URL, choose your quality, click Download, and the file saves to your default Downloads location.',

    tipsTitle: 'YouTube Download Tips & Tricks',
    tipsIntro: 'Get the best results from FastAudio with these practical tips for downloading YouTube content.',
    tips: [
      { title: 'Shorts',          body: 'YouTube Shorts URLs (youtube.com/shorts/...) are fully supported. Paste them exactly as they appear in your browser or the YouTube app.' },
      { title: 'Music',           body: 'For music downloads, choose 320kbps MP3 for the best audio quality. The difference between 320kbps and 192kbps is clearly audible on good speakers or headphones.' },
      { title: 'Live recordings', body: 'Completed YouTube live streams can be downloaded just like regular videos. Ongoing live streams cannot be downloaded while still live.' },
      { title: 'Long videos',     body: 'FastAudio handles long videos (lectures, podcasts, full albums) without a duration limit. Processing time scales with video length.' },
      { title: 'Region-locked',   body: 'Videos with geographic restrictions may fail to download depending on your server region. Try again or check if the video is available in your country.' },
      { title: 'Private videos',  body: 'Private YouTube videos cannot be downloaded. Only publicly accessible videos (including unlisted) are supported.' },
    ],

    faqs: [
      { q: 'Can I download YouTube videos in 4K or 2K?', a: 'FastAudio supports up to 1080p Full HD for MP4 downloads. 4K (2160p) and 1440p require special yt-dlp configuration and may not always be available depending on the video source.' },
      { q: 'Is it legal to download YouTube videos?', a: 'YouTube\'s Terms of Service prohibit downloading without explicit permission. However, downloading content you have rights to, or content licensed under Creative Commons, is generally acceptable. Always respect copyright and use downloaded content for personal, offline viewing only.' },
      { q: 'Does FastAudio support YouTube Music?', a: 'Yes. YouTube Music URLs (music.youtube.com/watch?v=...) are fully supported. Paste the YouTube Music link and download the track as an MP3.' },
      { q: 'Can I download a full YouTube playlist?', a: 'FastAudio downloads individual videos. For playlists, copy each video URL separately. Batch playlist downloading is not currently supported.' },
      { q: 'Why does the download button show an error?', a: 'YouTube periodically blocks automated server requests. If a download fails, wait 30 seconds and try again — it usually succeeds on a second attempt. Age-restricted and private videos cannot be downloaded.' },
      { q: 'What is the maximum video length I can download?', a: 'There is no hard duration limit. FastAudio can handle videos of any length, including multi-hour concerts or lectures. Longer videos will take more time to process.' },
      { q: 'Can I download YouTube Shorts as MP4?', a: 'Yes! YouTube Shorts are treated as regular videos. You can download them as MP4 (portrait/vertical video) or as MP3 audio.' },
    ],

    relatedLinks: [
      { href: '/instagram-video-downloader', label: 'Instagram Video Downloader',   desc: 'Download Instagram Reels & posts as MP4 or MP3.' },
      { href: '/tiktok-video-downloader',    label: 'TikTok Video Downloader',       desc: 'Download TikTok videos without watermark.' },
      { href: '/facebook-video-downloader',  label: 'Facebook Video Downloader',     desc: 'Download Facebook videos and Reels as MP4.' },
      { href: '/youtube-downloader',         label: 'YouTube to MP3 & MP4 Converter',desc: 'Full converter with all quality options.' },
      { href: '/extract-audio',              label: 'Extract Audio from Video',      desc: 'Pull MP3 audio from any video URL.' },
      { href: '/convert-video-to-mp3',       label: 'Convert Video to MP3',          desc: 'Step-by-step MP3 conversion guide.' },
    ],
  },

  // ────────────────── Instagram ────────────────────────────────────
  instagram: {
    platformName: 'Instagram',

    whatTitle: 'What Is an Instagram Video Downloader?',
    whatBody:
      'An Instagram video downloader is an online tool that lets you save Instagram Reels, feed videos, and IGTV content directly to your device — without needing the Instagram app or a Facebook account. FastAudio downloads public Instagram content as MP4 video (HD quality) or MP3 audio (up to 320kbps), entirely through your browser with no extensions or software required.',

    formatsIntro:
      'Instagram content can be downloaded as MP4 video (original quality, typically 720p) or extracted as MP3 audio — perfect for saving the soundtrack from a Reel or music post.',
    videoFormats: [
      { quality: '720p HD',   best: 'Original Reel quality', badge: 'HD' },
      { quality: '480p SD',   best: 'Smaller file size',     badge: null },
    ],

    howToTitle: 'How to Download Instagram Reels & Videos to MP4',
    howToIntro:
      'Saving an Instagram Reel or video with FastAudio takes under 20 seconds. Here is the exact process:',
    steps: [
      { title: 'Open Instagram',         text: 'Find the Reel, video post, or IGTV you want to save. Make sure it is from a public account.' },
      { title: 'Copy the post link',     text: 'Tap the three-dot (⋯) menu on the post → Copy Link. On desktop, copy the URL from your browser\'s address bar.' },
      { title: 'Paste into FastAudio',   text: 'Tap the search box at the top of this page and paste the Instagram URL.' },
      { title: 'Select MP4 or MP3',      text: 'Choose "Video (720p)" for the full MP4 file, or select an MP3 option to extract the audio only.' },
      { title: 'Download your file',     text: 'Tap the ⚡ Download button. The file saves directly to your device — no watermark, no Instagram branding.' },
    ],

    devicesIntro: 'FastAudio works across all devices with a modern browser. No Instagram account or login is required.',
    deviceIOS:     'In Safari on iPhone or iPad, paste the Instagram Reel URL and tap Download. The MP4 saves to your Files app, from where you can move it to Photos.',
    deviceAndroid: 'In Chrome on Android, paste the link, select your format, and tap Download. The video saves directly to your Downloads folder.',
    deviceDesktop: 'On any desktop browser, paste the Instagram URL, pick MP4 or MP3, and click Download. The file saves to your default downloads directory.',

    tipsTitle: 'Instagram Download Tips & Best Practices',
    tipsIntro: 'Follow these tips to get the most out of FastAudio\'s Instagram downloader.',
    tips: [
      { title: 'Public only',       body: 'Only publicly visible posts can be downloaded. If a profile is set to Private, its content is not accessible without login.' },
      { title: 'Reels vs Stories',  body: 'Reels are permanently posted and can be downloaded. Instagram Stories disappear after 24 hours and require login to view — they cannot be downloaded.' },
      { title: 'Correct URL',       body: 'Tap the three-dot menu on the exact post and select Copy Link. Sharing the profile URL instead of the specific post URL will not work.' },
      { title: 'Audio quality',     body: 'Instagram audio is typically encoded at 128kbps. Selecting 320kbps in FastAudio will still produce the best possible quality from the source.' },
      { title: 'IGTV videos',       body: 'Long-form IGTV videos are fully supported. Copy the IGTV post URL (not the IGTV channel URL) for best results.' },
      { title: 'Carousel posts',    body: 'For carousel posts with multiple videos, copy the link to the specific slide (visible when you open the full post page).' },
    ],

    faqs: [
      { q: 'Can I download Instagram Reels without watermark?', a: 'Yes. FastAudio downloads the original MP4 file from Instagram\'s CDN — it does not contain the visual Instagram watermark that appears in the app\'s native "Save" function.' },
      { q: 'Why does my Instagram download fail?', a: 'Instagram restricts access to most content from server IP addresses. Only fully public posts visible without any login can be reliably downloaded. Private accounts, Stories, and login-gated content will fail.' },
      { q: 'Can I download IGTV videos?', a: 'Yes. IGTV (long-form Instagram videos) are supported as long as the post is publicly accessible. Copy the URL of the specific IGTV video post.' },
      { q: 'Does FastAudio support Instagram Stories?', a: 'No. Instagram Stories expire after 24 hours and require a login session to access. They cannot be downloaded through FastAudio.' },
      { q: 'What quality is the downloaded Instagram video?', a: 'Instagram stores Reels and videos at up to 720p HD. FastAudio downloads the best available resolution from Instagram\'s servers — typically 720p for Reels and standard feed videos.' },
      { q: 'Can I download someone else\'s private Instagram post?', a: 'No. FastAudio only accesses publicly available content. Attempting to download private posts would violate Instagram\'s Terms of Service and user privacy.' },
    ],

    relatedLinks: [
      { href: '/youtube-video-downloader',  label: 'YouTube Video Downloader',      desc: 'Download YouTube videos in MP4 up to 1080p.' },
      { href: '/tiktok-video-downloader',   label: 'TikTok Video Downloader',        desc: 'Download TikTok videos without watermark.' },
      { href: '/facebook-video-downloader', label: 'Facebook Video Downloader',      desc: 'Download Facebook videos and Reels as MP4.' },
      { href: '/instagram-downloader',      label: 'Instagram to MP3 & MP4 Converter',desc: 'Full converter with all quality options.' },
      { href: '/extract-audio',             label: 'Extract Audio from Video',       desc: 'Pull MP3 audio from any video URL.' },
      { href: '/convert-video-to-mp3',      label: 'Convert Video to MP3',           desc: 'Step-by-step MP3 conversion guide.' },
    ],
  },

  // ────────────────── TikTok ───────────────────────────────────────
  tiktok: {
    platformName: 'TikTok',

    whatTitle: 'What Is a TikTok Video Downloader?',
    whatBody:
      'A TikTok video downloader lets you save any public TikTok video directly to your device — without the TikTok watermark, without the app, and completely free. FastAudio downloads TikTok content as a clean MP4 video file or as an MP3 audio file (up to 320kbps). Simply paste the TikTok URL, choose your format, and the file is saved in seconds.',

    formatsIntro:
      'TikTok videos can be saved as MP4 (the full video, watermark-free) or converted to MP3 audio — ideal for saving trending sounds or background music from TikTok.',
    videoFormats: [
      { quality: '720p HD',    best: 'Crisp full-resolution video',  badge: 'HD' },
      { quality: '480p SD',    best: 'Compact file for sharing',     badge: null },
    ],

    howToTitle: 'How to Download TikTok Videos Without Watermark',
    howToIntro:
      'FastAudio downloads TikTok videos without the native watermark that the TikTok app adds. Follow these steps:',
    steps: [
      { title: 'Open the TikTok video',    text: 'Find the video in the TikTok app or on tiktok.com that you want to save.' },
      { title: 'Copy the share link',      text: 'In the TikTok app, tap the Share button (arrow) → Copy Link. On tiktok.com, copy the URL from your browser address bar.' },
      { title: 'Paste into FastAudio',     text: 'Open FastAudio in your browser, tap the search box, and paste the TikTok URL.' },
      { title: 'Choose your format',       text: 'Select "Video (720p)" to download the full MP4, or choose an MP3 quality to save the audio track only.' },
      { title: 'Download without watermark',text: 'Tap ⚡ Download. FastAudio fetches the original video stream — no TikTok watermark, no username overlay.' },
    ],

    devicesIntro: 'FastAudio\'s TikTok downloader is fully mobile-friendly. Save TikTok videos directly from your phone browser without installing anything.',
    deviceIOS:     'In Safari on iPhone, paste the TikTok share link, select MP4 or MP3, and tap Download. The file saves to your Files app. You can then move it to Photos or share it.',
    deviceAndroid: 'In Chrome on Android, paste the TikTok URL and tap Download. The watermark-free MP4 or MP3 saves directly to your Downloads folder.',
    deviceDesktop: 'On Windows, Mac, or Linux, paste the TikTok URL into the search box, choose your format, and click Download. The file saves to your browser\'s default downloads location.',

    tipsTitle: 'TikTok Download Tips & Tricks',
    tipsIntro: 'Get the best results when saving TikTok content with FastAudio.',
    tips: [
      { title: 'Short links',         body: 'TikTok short links (vm.tiktok.com/..., vt.tiktok.com/...) are fully supported — paste them exactly as copied from the share menu.' },
      { title: 'Trending sounds',     body: 'To save just the audio from a TikTok, choose the MP3 option. FastAudio extracts the original sound track at up to 320kbps.' },
      { title: 'Duets and collabs',   body: 'Duet and stitch videos can be downloaded as long as the account is public. The downloaded file contains the full duet/stitch frame.' },
      { title: 'Private accounts',    body: 'Videos from private TikTok accounts cannot be downloaded. Make sure the creator\'s profile is public before attempting to download.' },
      { title: 'Long videos',         body: 'TikTok now supports videos up to 10 minutes. FastAudio handles all lengths — longer videos will take a few extra seconds to process.' },
      { title: 'Photo slideshows',    body: 'TikTok photo slideshows (carousel posts) are not video files and cannot be downloaded as MP4. Only video-based TikTok posts are supported.' },
    ],

    faqs: [
      { q: 'Is the downloaded TikTok video really watermark-free?', a: 'Yes. FastAudio fetches the original video stream from TikTok\'s CDN, which does not include the username/watermark overlay that TikTok\'s built-in save feature adds. Your downloaded MP4 will be clean.' },
      { q: 'Why does my TikTok download fail?', a: 'TikTok blocks automated requests from many cloud server IP addresses. If a download fails, wait 30 seconds and try again — it typically succeeds on the second attempt. Private accounts and deleted videos cannot be downloaded.' },
      { q: 'Can I download TikTok Lites or vm.tiktok.com links?', a: 'Yes, both standard TikTok URLs and short links (vm.tiktok.com, vt.tiktok.com) are supported. Paste the link exactly as it appears when you tap Copy Link in the app.' },
      { q: 'Can I download TikTok audio to use in my videos?', a: 'You can download TikTok audio for personal listening. Using copyrighted music in your own published content may violate copyright law and TikTok\'s terms — always check the licensing status of the sound before reusing it commercially.' },
      { q: 'What resolution are TikTok videos?', a: 'Most TikTok videos are shot and stored at 720p or 1080p. FastAudio downloads the best resolution available from TikTok\'s servers, typically 720p HD.' },
      { q: 'Does FastAudio support TikTok Live recordings?', a: 'Completed TikTok Live recordings saved to a creator\'s profile can be downloaded if the post is publicly accessible. Ongoing live streams cannot be downloaded in real time.' },
    ],

    relatedLinks: [
      { href: '/youtube-video-downloader',  label: 'YouTube Video Downloader',      desc: 'Download YouTube videos in MP4 up to 1080p.' },
      { href: '/instagram-video-downloader',label: 'Instagram Video Downloader',     desc: 'Download Instagram Reels without watermark.' },
      { href: '/facebook-video-downloader', label: 'Facebook Video Downloader',      desc: 'Download Facebook videos and Reels as MP4.' },
      { href: '/tiktok-downloader',         label: 'TikTok to MP3 & MP4 Converter', desc: 'Full converter with all quality options.' },
      { href: '/extract-audio',             label: 'Extract Audio from Video',       desc: 'Pull MP3 audio from any video URL.' },
      { href: '/mobile-convert',            label: 'Convert on Mobile',              desc: 'Guide for downloading on iPhone & Android.' },
    ],
  },

  // ────────────────── Facebook ─────────────────────────────────────
  facebook: {
    platformName: 'Facebook',

    whatTitle: 'What Is a Facebook Video Downloader?',
    whatBody:
      'A Facebook video downloader is an online tool that lets you save Facebook videos, Watch posts, and Facebook Reels directly to your device — without needing a Facebook account, a browser extension, or any software. FastAudio supports both MP4 video downloads (SD 480p and HD 720p) and MP3 audio extraction (up to 320kbps) from publicly accessible Facebook content.',

    formatsIntro:
      'Facebook videos are typically available in two video resolutions — Standard Definition (480p) and High Definition (720p) — plus full MP3 audio extraction at three quality levels.',
    videoFormats: [
      { quality: '720p HD',   best: 'Crisp, full-quality video',  badge: 'HD' },
      { quality: '480p SD',   best: 'Smaller file, still clear',  badge: null },
    ],

    howToTitle: 'How to Download Facebook Videos in MP4 or MP3',
    howToIntro:
      'Download any public Facebook video in three easy steps using FastAudio:',
    steps: [
      { title: 'Find the Facebook video',   text: 'Open Facebook and locate the video post, Facebook Watch video, or Reel you want to save. Make sure it is publicly visible.' },
      { title: 'Copy the video link',       text: 'Click the three-dot (⋯) menu on the post → Copy link. On mobile, tap Share → Copy link. You can also copy the URL from your browser address bar.' },
      { title: 'Paste into FastAudio',      text: 'Open FastAudio in your browser, tap the search box, and paste the Facebook URL.' },
      { title: 'Choose video or audio',     text: 'Select HD (720p) or SD (480p) for MP4 video, or choose 320/192/128kbps to download MP3 audio only.' },
      { title: 'Download your file',        text: 'Click ⚡ Download. Your file will be saved directly to your device — no Facebook account or login required.' },
    ],

    devicesIntro: 'FastAudio\'s Facebook downloader works on every device through any modern browser. No Facebook login is required.',
    deviceIOS:     'In Safari on iPhone or iPad, paste the Facebook video URL, choose HD or SD video (or MP3), and tap Download. The file will save to your Files app.',
    deviceAndroid: 'In Chrome on Android, paste the Facebook link and tap Download. The MP4 or MP3 file saves to your Downloads folder automatically.',
    deviceDesktop: 'On any desktop browser (Chrome, Firefox, Safari, Edge), paste the URL, select your quality, and click Download. Files save to your default downloads folder.',

    tipsTitle: 'Facebook Video Download Tips',
    tipsIntro: 'Get the most reliable results when downloading Facebook videos with FastAudio.',
    tips: [
      { title: 'Public posts only',   body: 'Only videos from public Facebook profiles and pages can be downloaded. Videos limited to Friends, Groups, or private accounts cannot be accessed.' },
      { title: 'Correct URL format',  body: 'Copy the link from the post\'s three-dot menu, not the profile URL. The post URL should contain a numeric video ID or /videos/ in the path.' },
      { title: 'Facebook Reels',      body: 'Facebook Reels are fully supported. Use the Share → Copy Link option on the Reel post to get the correct URL.' },
      { title: 'fb.watch links',      body: 'Facebook short links (fb.watch/...) are supported and will be automatically resolved to the full video.' },
      { title: 'Thumbnails',          body: 'Facebook thumbnails may not appear in FastAudio\'s preview due to CDN authentication restrictions — this is normal and does not affect the download.' },
      { title: 'Group videos',        body: 'Videos posted inside private Facebook Groups cannot be downloaded unless the Group is set to Public. Public Group videos are supported.' },
    ],

    faqs: [
      { q: 'What Facebook video types can I download?', a: 'FastAudio supports standard Facebook video posts, Facebook Watch videos, Facebook Reels, and public Group videos. Private videos, Stories, and content behind login walls cannot be downloaded.' },
      { q: 'Why does my Facebook download fail?', a: 'The most common reason is that the video is not publicly accessible. Check that the post is set to "Public" (not "Friends only"). If it is public and still fails, try again — server-side rate limiting occasionally causes temporary failures.' },
      { q: 'Can I download HD Facebook videos?', a: 'Yes. If the original video was uploaded at 720p or higher, FastAudio will offer an HD (720p) download option alongside the standard SD (480p) option.' },
      { q: 'Can I download Facebook Stories?', a: 'No. Facebook Stories expire after 24 hours and require an authenticated session to view. They cannot be downloaded through FastAudio.' },
      { q: 'How do I copy the Facebook video URL on mobile?', a: 'In the Facebook app, tap the three-dot (⋯) menu on the post and select "Copy link". If you\'re in a browser, tap the address bar and copy the URL while the video post is open.' },
      { q: 'Is it legal to download Facebook videos?', a: 'Downloading public Facebook content for personal, offline viewing is generally accepted. However, distributing or republishing downloaded content without the creator\'s permission may violate Facebook\'s Terms of Service and applicable copyright laws.' },
    ],

    relatedLinks: [
      { href: '/youtube-video-downloader',  label: 'YouTube Video Downloader',       desc: 'Download YouTube videos in MP4 up to 1080p.' },
      { href: '/instagram-video-downloader',label: 'Instagram Video Downloader',      desc: 'Download Instagram Reels without watermark.' },
      { href: '/tiktok-video-downloader',   label: 'TikTok Video Downloader',         desc: 'Download TikTok videos without watermark.' },
      { href: '/facebook-downloader',       label: 'Facebook to MP3 & MP4 Converter', desc: 'Full converter with all quality options.' },
      { href: '/extract-audio',             label: 'Extract Audio from Video',        desc: 'Pull MP3 audio from any video URL.' },
      { href: '/mobile-convert',            label: 'Convert on Mobile',               desc: 'Guide for downloading on iPhone & Android.' },
    ],
  },
}
